import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe, NgTemplateOutlet } from '@angular/common';

import { ButtonModule, IconModule, OkDialog, PopoverDirective, SpinnerDirective } from '@hglobal/common';
import { TranslateInPipe } from '@fb/pipes/translate-in.pipe';

import { AiChatStore } from '../../store';
import { formatAiChatThreadListTitle } from '../../helpers/format-ai-chat-thread-list-title';
import { workChatBadge } from '../../helpers/work-context';

@Component({
  selector: 'ai-chat-history-panel',
  imports: [
    NgTemplateOutlet,
    ButtonModule,
    IconModule,
    PopoverDirective,
    SpinnerDirective,
    TranslateInPipe,
  ],
  providers: [DatePipe],
  templateUrl: './ai-chat-history-panel.component.html',
  styleUrl: './ai-chat-history-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatHistoryPanelComponent {
  /** When true the panel fills the popup area with a back-arrow header instead of the sidebar collapse toggle. */
  readonly isPopup = input(false);
  readonly backClick = output<void>();

  readonly #store = inject(AiChatStore);
  readonly #datePipe = inject(DatePipe);
  readonly #okDialog = inject(OkDialog);
  readonly #destroyRef = inject(DestroyRef);

  readonly chats = this.#store.chats;
  readonly isLoading = this.#store.chatsLoading;
  readonly isLoadingMore = this.#store.chatsLoadingMore;
  readonly hasError = this.#store.chatsError;
  readonly hasMore = this.#store.chatsHasMore;
  readonly selectedChatId = this.#store.selectedChatId;
  readonly generatingChatId = this.#store.generatingChatId;
  readonly historySidebarCollapsed = this.#store.historySidebarCollapsed;

  /** ID of the chat whose title is currently being edited inline. */
  readonly renamingChatId = signal<string | null>(null);

  /** Original title captured when rename starts — used to skip no-op API calls. */
  #renamingOriginalTitle: string | null = null;

  /** ID of the chat pending delete confirmation (drives inline confirm overlay). */
  readonly deletingChatId = signal<string | null>(null);

  protected readonly workChatBadge = workChatBadge;

  onNewChat(): void {
    this.#store.openNewChat();
    if (this.isPopup()) {
      this.backClick.emit();
    }
  }

  onSelectChat(chatId: string): void {
    if (this.renamingChatId() === chatId) {
      return;
    }
    this.#store.selectChat(chatId);
    if (this.isPopup()) {
      this.backClick.emit();
    }
  }

  toggleSidebar(): void {
    this.#store.toggleHistorySidebarCollapsed();
  }

  onBodyScroll(event: Event): void {
    if (!this.hasMore() || this.isLoading()) {
      return;
    }
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      this.#store.loadMoreChats();
    }
  }

  /** Format chat title: converts UTC wall-clock titles from backend to local time; falls back to updatedAt. */
  chatDisplayTitle(chat: { title: string; updatedAt: string }): string {
    if (chat.title) {
      return formatAiChatThreadListTitle(chat.title, this.#datePipe);
    }

    return this.#datePipe.transform(chat.updatedAt, 'dd.MM.yyyy HH:mm') ?? chat.updatedAt;
  }

  startRename(event: Event, chatId: string, currentTitle: string): void {
    event.stopPropagation();
    this.#renamingOriginalTitle = currentTitle;
    this.renamingChatId.set(chatId);
  }

  confirmRename(chatId: string, value: string): void {
    const trimmed = value.trim();
    const originalTitle = this.#renamingOriginalTitle;

    this.renamingChatId.set(null);
    this.#renamingOriginalTitle = null;

    if (!trimmed || this.#hasControlChars(trimmed)) {
      return;
    }

    if (trimmed !== originalTitle) {
      this.#store.renameChat(chatId, trimmed);
    }
  }

  cancelRename(): void {
    this.renamingChatId.set(null);
    this.#renamingOriginalTitle = null;
  }

  onRenameKeydown(event: KeyboardEvent, chatId: string, value: string): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.confirmRename(chatId, value);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelRename();
    }
  }

  togglePin(event: Event, chatId: string, currentIsPinned: boolean): void {
    event.stopPropagation();
    this.#store.pinChat(chatId, !currentIsPinned);
  }

  requestDelete(event: Event, chatId: string): void {
    event.stopPropagation();

    if (this.isPopup()) {
      // Inline confirmation — avoids opening a second CDK overlay that would visually cover the popup
      this.deletingChatId.set(chatId);
    } else {
      // Full-page: use OkDialog (attaches to <body>, escapes any CSS transform containing block)
      this.#okDialog
        .open({
          headerKey: 'chatBot.history.deleteTitle',
          subMessageKey: 'chatBot.history.deleteBody',
          btnOkKey: 'chatBot.history.deleteConfirm',
          btnCancelKey: 'chatBot.history.deleteCancel',
        })
        .afterClosed()
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(confirmed => {
          if (confirmed) {
            this.#store.deleteChat(chatId);
          }
        });
    }
  }

  confirmDelete(): void {
    const chatId = this.deletingChatId();

    this.deletingChatId.set(null);

    if (chatId) {
      this.#store.deleteChat(chatId);
    }
  }

  cancelDelete(): void {
    this.deletingChatId.set(null);
  }

  #hasControlChars(value: string): boolean {
    return [...value].some(char => {
      const code = char.charCodeAt(0);

      return code <= 0x1f || code === 0x7f;
    });
  }
}
